import React, { useContext, createContext, useState } from 'react';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth } from '../config/firebase-init';
import Login from '../pages/Login';
import ElderlyLogin from '../pages/ElderlyLogin';

const authContext = createContext();

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export function useAuth() {
  return useContext(authContext);
}

function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [email, setMail] = useState(null);
  
  // 檢查登入狀態是否過期
  const checkAuthExpiry = () => {
    const lastActiveTime = localStorage.getItem('lastActiveTime');
    if (lastActiveTime) {
      const now = new Date().getTime();
      const lastActive = parseInt(lastActiveTime);
      const oneDayInMs = 24 * 60 * 60 * 1000; // 24小時
      
      if (now - lastActive > oneDayInMs) {
        // 超過 24 小時，清理登入狀態
        localStorage.removeItem('lastActiveTime');
        signOut(auth);
        return false;
      }
      
      // 更新最後活動時間
      localStorage.setItem('lastActiveTime', now.toString());
      return true;
    }
    return false;
  };
  
  // 初始化時檢查過期狀態
  useState(() => {
    checkAuthExpiry();
  });
  
  onAuthStateChanged(auth,
    function (user) {
      if (user && checkAuthExpiry()) {
        setUser(user.displayName);
        setMail(user.email);
        // 設定最後活動時間
        const now = new Date().getTime();
        localStorage.setItem('lastActiveTime', now.toString());
      } else {
        setUser(null);
        setMail(null);
      }
    },
    function (error) {
      console.log(error);
      alert('Unable to log in: ' + error);
    }
  );

  const signin = cb => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      
      return setPersistence(auth, browserLocalPersistence)
        .then(() => {
          return signInWithPopup(auth, provider);
        })
        .then(result => {
          const user = result.user;
          if (user) {
            setUser(user.displayName);
            setMail(user.email);
            // 設定登入時間
            const now = new Date().getTime();
            localStorage.setItem('lastActiveTime', now.toString());
            
            console.log('登入成功:', user.email);
            cb && cb(user);
            return user;
          } else {
            throw new Error('無法獲取用戶信息');
          }
        })
        .catch(error => {
          console.error('登入錯誤:', error);
          
          // 根據錯誤類型提供友好的錯誤訊息
          let errorMessage;
          switch (error.code) {
            case 'auth/popup-closed-by-user':
              errorMessage = '登入已取消';
              break;
            case 'auth/popup-blocked':
              errorMessage = '彈出視窗被阻擋，請允許彈出視窗後再試';
              break;
            case 'auth/cancelled-popup-request':
              errorMessage = '登入請求已被取消';
              break;
            case 'auth/network-request-failed':
              errorMessage = '網路連接錯誤，請檢查網路設定';
              break;
            case 'auth/too-many-requests':
              errorMessage = '登入嘗試過於頻繁，請稍後再試';
              break;
            case 'auth/user-disabled':
              errorMessage = '此帳號已被停用，請聯繫管理員';
              break;
            case 'auth/operation-not-allowed':
              errorMessage = 'Google 登入未啟用，請聯繫管理員';
              break;
            default:
              errorMessage = `登入失敗: ${error.message || '未知錯誤'}`;
          }
          
          // 顯示用戶友好的錯誤訊息
          if (typeof window !== 'undefined' && window.alert) {
            alert(errorMessage);
          }
          
          // 拋出錯誤供調用者處理
          throw new Error(errorMessage);
        });
    } catch (error) {
      console.error('登入設定錯誤:', error);
      const message = '登入系統初始化失敗，請刷新頁面後重試';
      if (typeof window !== 'undefined' && window.alert) {
        alert(message);
      }
      throw new Error(message);
    }
  };

  const signout = cb => {
    return signOut(auth)
      .then(() => {
        // 清理最後活動時間
        localStorage.removeItem('lastActiveTime');
        setUser(null);
        setMail(null);
        console.log('登出成功');
        cb && cb();
        return true;
      })
      .catch(error => {
        console.error('登出錯誤:', error);
        
        // 根據錯誤類型提供友好的錯誤訊息
        let errorMessage;
        switch (error.code) {
          case 'auth/network-request-failed':
            errorMessage = '網路連接錯誤，無法登出';
            break;
          case 'auth/too-many-requests':
            errorMessage = '請求過於頻繁，請稍後再試';
            break;
          default:
            errorMessage = `登出失敗: ${error.message || '未知錯誤'}`;
        }
        
        // 即使登出失敗，也要清理本地狀態
        localStorage.removeItem('lastActiveTime');
        setUser(null);
        setMail(null);
        
        // 顯示錯誤訊息但不阻擋登出流程
        if (typeof window !== 'undefined' && window.alert) {
          alert(`${errorMessage}\n\n已清理本地登入狀態`);
        }
        
        console.warn('登出錯誤，但已清理本地狀態');
        cb && cb(); // 仍然執行回調
        return false; // 返回 false 表示登出有錯誤但已處理
      });
  };

  return {
    user,
    email,
    signin,
    signout
  };
}

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
export function PrivateRoute({ children, ...rest }) {
  let auth = useAuth();
  // if (auth.user === null) {
  //   return <div />;
  // }
  return auth.user && auth.user !== '' ? children : <Login />;
}

// 老人版登入路由
export function ElderlyPrivateRoute({ children, ...rest }) {
  let auth = useAuth();
  return auth.user && auth.user !== '' ? children : <ElderlyLogin />;
}
